import { Component, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, NgZone } from "@angular/core";
import { ITCIAssetTag, TCI_ASSET_TAGS_COMPONENT_LAYOUT, TCI_ASSET_TAGS_COMPONENT_MODE, TAG_FILTER_SORT } from "@tci/common";
import { FormControl } from "@angular/forms";
import { setTimeoutInZone } from "../../../ng-util/tools";
import { Subject, Subscription } from "rxjs";
import * as lodash from "lodash";

@Component({
    selector: "ng-search-select-tags",
    templateUrl: "./ng-search-select-tags.component.html",
    styleUrls: ["./ng-search-select-tags.component.css"]
})
export class NgSearchSelectTagsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() mode: TCI_ASSET_TAGS_COMPONENT_MODE;
    @Input() layout: TCI_ASSET_TAGS_COMPONENT_LAYOUT;
    @Input() allTags: ITCIAssetTag[];
    @Input() selectedTags: ITCIAssetTag[];
    @Input() tagsListWidth: string;
    @Input() tagsListHeight: string;
    @Input() tagNameLengthLimit;
    @Input() selectAllEnabled = true;
    @Input() clearAllEnabled = true;
    @Input() showOnlySelected = true;
    @Input() limitedSet = false;

    @Input() showSelectedOnFiltering;
    @Input() sortSelectedOnFiltering;

    @Input() isVisible = true;

    @Output() create: EventEmitter<ITCIAssetTag> = new EventEmitter();
    @Output() tagsSelectionChange: EventEmitter<ITCIAssetTag[]> = new EventEmitter();

    TCI_ASSET_TAGS_COMPONENT_MODE = TCI_ASSET_TAGS_COMPONENT_MODE;
    TCI_ASSET_TAGS_COMPONENT_LAYOUT = TCI_ASSET_TAGS_COMPONENT_LAYOUT;

    private _localSelectedTags: ITCIAssetTag[] = [];
    get localSelectedTags(): ITCIAssetTag[] {
        return this._localSelectedTags;
    }
    set localSelectedTags(tags: ITCIAssetTag[]) {
        this._localSelectedTags = tags;
        this.fillSelectedNames();
    }

    showFiltered = false;
    filteredTags: ITCIAssetTag[] = [];
    hasExactMatch = false;
    keyword = "";
    selectionChangeSubject: Subject<any> = new Subject<any>();

    ELEMENT_IDS = {
        field: "tags-search-field",
        new: "tags-search-new",
        remove: "tag-remove"
    };

    // To avoid using a function at template to check if selected
    selectedNames: { [key: string]: boolean } = {};

    // Action tracking
    private localCreatedTags: ITCIAssetTag[] = [];
    private localRemovedTags: ITCIAssetTag[] = [];

    // Validation check
    tagsInput = new FormControl();

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    constructor(private ngZone: NgZone) {
        this.subscriptions.push(
            this.tagsInput.valueChanges
                .subscribe(() => {
                    this.keyword = this.tagsInput.value;
                    if (this.keyword && this.keyword.length > this.tagNameLengthLimit) {
                        this.keyword = this.keyword.substring(0, this.tagNameLengthLimit);
                        this.tagsInput.setValue(this.keyword, { emitEvent: false });
                    } else {
                        this.searchTagsList();
                    }

                    this.validateInput();
                    this.showFiltered = this.keyword.length > 0;
                })
        );
    }

    ngOnInit() {
        this.localSelectedTags = lodash.cloneDeep(this.selectedTags);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selectedTags) {
            this.localSelectedTags = lodash.cloneDeep(this.selectedTags);
        }

        if (("allTags" in changes || "selectedTags" in changes) && this.keyword.length === 0) {
            this.sortTags();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    clearTagsSelection(): void {
        if (this.filteredTags.length > 0) {
            this.filteredTags.forEach(fTag => {
                const index = this.localSelectedTags.findIndex(sTag => sTag.name === fTag.name);
                if (index >= 0) {
                    this.localSelectedTags.splice(index, 1);
                }
            });
        } else {
            this.localSelectedTags = [];
        }
        this.tagsSelectionChange.emit(this.localSelectedTags);
    }

    createNewTag($event): void {
        $event.stopPropagation();
        const newTag: ITCIAssetTag = { name: this.keyword, assetCount: 1 };
        this.localSelectedTags = lodash.union(this.localSelectedTags, [newTag]);
        this.localCreatedTags.push(newTag);
        this.allTags.push(newTag);
        this.create.emit(newTag);
        this.resetKeyword();
    }

    resetKeyword(): void {
        this.tagsInput.setValue("");
        this.showFiltered = false;
        this.searchTagsList();
    }

    searchTagsList(): void {
        this.hasExactMatch = this.allTags.some(tag => tag.name === this.keyword);
        let tempCollection = this.allTags.filter(tag => tag.name.toLowerCase().includes(this.keyword.toLowerCase()));

        if (this.showSelectedOnFiltering) {
            const name = "name";
            let selected: ITCIAssetTag[];
            const assignElements = () => {
                selected = lodash.sortBy(this.localSelectedTags, name);
                const selectedNames = selected.map(s => s.name);
                tempCollection = tempCollection.filter(t => !selectedNames.includes(t.name));
            };

            switch (this.sortSelectedOnFiltering) {
                case TAG_FILTER_SORT.alphabetical:
                    tempCollection.push(...this.localSelectedTags);
                    tempCollection = lodash.uniqBy(tempCollection, name);
                    tempCollection = lodash.orderBy(tempCollection, [item => item.name.toLowerCase()], ["asc"]);
                    break;
                case TAG_FILTER_SORT.top:
                    assignElements();
                    tempCollection.unshift(...selected);
                    break;
                case TAG_FILTER_SORT.bottom:
                    assignElements();
                    tempCollection.push(...selected);
                    break;
            }
        }

        this.filteredTags = tempCollection;
    }

    selectAllTags(): void {
        this.localSelectedTags.push(
            ...this.filteredTags.filter(fTag => this.localSelectedTags.findIndex(lTag => lTag.name === fTag.name) < 0)
        );
        this.tagsSelectionChange.emit(this.localSelectedTags);
    }

    sortTags(): void {
        this.allTags = lodash.sortBy(this.allTags, "name");
        this.searchTagsList();
    }

    toggle($event, tag: ITCIAssetTag): void {
        $event.stopPropagation();

        // IPAS-15806 - Evaluate the click target and if app-details + selected it
        // should only allow the [x] button
        if (
            this.mode === TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL &&
            this.selectedNames[tag.name] &&
            !$event.target.classList.contains(this.ELEMENT_IDS.remove)
        ) {
            return;
        }

        const targetTag: ITCIAssetTag = this.localSelectedTags.find(t => t.name === tag.name);
        if (targetTag) { // Removing
            this.localSelectedTags = this.localSelectedTags.filter(t => t.name !== tag.name);
            this.localRemovedTags.push(targetTag);
            this.cleanRemovedTags();
        } else {
            this.localSelectedTags = lodash.concat(this.localSelectedTags, tag);
        }

        this.fillSelectedNames();
        this.tagsSelectionChange.emit(this.localSelectedTags);
    }

    saveTag() {
        if (this.mode === TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL) {
            const btn = document.getElementById(this.ELEMENT_IDS.new + "_" + this.mode);
            if (btn) {
                const event = new CustomEvent("click");
                btn.dispatchEvent(event);
            }
        }
    }

    // Autofocus the field
    focusField() {
        if (this.mode === TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL) {
            setTimeoutInZone(this.ngZone, () => {
                const searchField = document.getElementById(this.ELEMENT_IDS.field + "_" + this.mode);
                if (searchField) {
                    searchField.focus();
                }
            });
        }
    }

    private fillSelectedNames() {
        this.selectedNames = {};
        this.localSelectedTags.forEach(t => this.selectedNames[t.name] = true);
    }

    /**
     * Detect if a just-created tag was removed
     */
    private cleanRemovedTags() {
        if (this.localCreatedTags.length > 0 && this.localRemovedTags.length > 0) { // IPAS-15752
            const cleanCollection = (collection: ITCIAssetTag[], tag: ITCIAssetTag) => {
                const index = collection.findIndex(t => t.name === tag.name);
                if (index >= 0) {
                    collection.splice(index, 1);
                    return true;
                }
                return false;
            };

            for (const i in this.localRemovedTags) {
                if (i in this.localRemovedTags) {
                    const cleanLocalCreated = cleanCollection(this.localCreatedTags, this.localRemovedTags[i]);
                    if (cleanLocalCreated) {
                        cleanCollection(this.allTags, this.localRemovedTags[i]);
                        cleanCollection(this.filteredTags, this.localRemovedTags[i]);
                        cleanCollection(this.selectedTags, this.localRemovedTags[i]);
                        cleanCollection(this.localSelectedTags, this.localRemovedTags[i]);
                    }
                }
            }

            this.localRemovedTags = []; // Reset the tracker
        }
    }

    // IPAS-16758 - Validate input text
    private validateInput(): boolean {
        if (this.mode !== TCI_ASSET_TAGS_COMPONENT_MODE.FULLY_FUNCTIONAL) {
            return true;
        }

        const expression = /^[a-zA-Z-_\d]+$/gi;
        const isValid = this.keyword.trim().length === 0 || expression.test(this.keyword);
        if (isValid) {
            this.tagsInput.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        } else {
            this.tagsInput.setErrors({ validation: true }, { emitEvent: false });
        }
        return isValid;
    }
}
